/**
 * Three jigsaw variants, all producing paths in unit cell space [≈0–1].
 *
 *  classic   – Interlocking knobs / sockets (cubic bezier bumps).
 *  square    – Plain rectangles (simplest freeform drag).
 *  irregular – Organic random curved cuts that still tile perfectly:
 *              internal grid joints are displaced, each shared edge is
 *              a random cubic bezier; both neighbors traverse the *same*
 *              curve in opposite directions so pieces fit seamlessly.
 */

import { mulberry32, hashSeed } from "./rng";
import { createSquarePieces, createInterlockPieces } from "./jigsawPieceGenerators";

export { hashSeed, mulberry32 } from "./rng";

/**
 * @param {number} rows
 * @param {number} cols
 * @param {string|number} seed
 * @param {"classic"|"square"|"irregular"} variant
 * @returns {{ id:number, row:number, col:number, pathD:string, layout?:string }[]}
 */
export function generateInterlockingPiecePaths(rows, cols, seed, variant = "classic") {
  if (variant === "square") return createSquarePieces(rows, cols);
  if (variant === "irregular") return generateOrganicIrregularPaths(rows, cols, seed);
  return createInterlockPieces(rows, cols, seed);
}
/* ─── Irregular (organic random curved boundaries) ───────────────────
 *
 * Grid joints (rows+1)×(cols+1) are displaced randomly (boundary fixed).
 * Each internal horizontal/vertical edge is a cubic bézier with random
 * perpendicular offsets.  Both adjacent pieces reference the *same*
 * control points (one forward, one reversed) so cuts mate perfectly.
 * ──────────────────────────────────────────────────────────────────── */

function generateOrganicIrregularPaths(rows, cols, seed) {
  const rng = mulberry32(hashSeed([seed, rows, cols, "organic"]));

  /* --- displaced joints --- */
  const maxD = 0.16;
  const joints = [];
  for (let r = 0; r <= rows; r++) {
    joints[r] = [];
    for (let c = 0; c <= cols; c++) {
      const onEdge = r === 0 || r === rows || c === 0 || c === cols;
      joints[r][c] = {
        x: c + (onEdge ? 0 : (rng() - 0.5) * maxD * 2),
        y: r + (onEdge ? 0 : (rng() - 0.5) * maxD * 2),
      };
    }
  }

  /* --- horizontal edges  hEdge[r][c] : joint(r,c) → joint(r,c+1) --- */
  const hEdge = [];
  for (let r = 0; r <= rows; r++) {
    hEdge[r] = [];
    for (let c = 0; c < cols; c++) {
      const border = r === 0 || r === rows;
      const amp = border ? 0 : 0.08 + rng() * 0.16;
      const s1 = rng() < 0.5 ? -1 : 1;
      const s2 = rng() < 0.4 ? -s1 : s1;
      hEdge[r][c] = {
        cp1Off: border ? 0 : s1 * amp * (0.5 + rng() * 0.9),
        cp2Off: border ? 0 : s2 * amp * (0.5 + rng() * 0.9),
      };
    }
  }

  /* --- vertical edges  vEdge[r][c] : joint(r,c) → joint(r+1,c) --- */
  const vEdge = [];
  for (let r = 0; r < rows; r++) {
    vEdge[r] = [];
    for (let c = 0; c <= cols; c++) {
      const border = c === 0 || c === cols;
      const amp = border ? 0 : 0.08 + rng() * 0.16;
      const s1 = rng() < 0.5 ? -1 : 1;
      const s2 = rng() < 0.4 ? -s1 : s1;
      vEdge[r][c] = {
        cp1Off: border ? 0 : s1 * amp * (0.5 + rng() * 0.9),
        cp2Off: border ? 0 : s2 * amp * (0.5 + rng() * 0.9),
      };
    }
  }

  /* --- build per-piece paths --- */
  const pieces = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const tl = localJoint(joints, r, c, r, c);
      const tr = localJoint(joints, r, c + 1, r, c);
      const br = localJoint(joints, r + 1, c + 1, r, c);
      const bl = localJoint(joints, r + 1, c, r, c);

      let d = `M ${f(tl.x)} ${f(tl.y)}`;
      d += cubicEdgeSvg(tl, tr, hEdge[r][c], false);       // top
      d += cubicEdgeSvg(tr, br, vEdge[r][c + 1], false);    // right
      d += cubicEdgeSvg(br, bl, hEdge[r + 1][c], true);     // bottom (reversed)
      d += cubicEdgeSvg(bl, tl, vEdge[r][c], true);         // left   (reversed)
      d += " Z";

      pieces.push({
        id: r * cols + c,
        row: r,
        col: c,
        pathD: d,
        layout: "grid",
      });
    }
  }
  return pieces;
}

/** Convert global joint (jr, jc) into piece-local coordinates for cell (pr, pc). */
function localJoint(joints, jr, jc, pr, pc) {
  return { x: joints[jr][jc].x - pc, y: joints[jr][jc].y - pr };
}

function f(v) {
  return Math.round(v * 1e4) / 1e4;
}

/**
 * Emit `C cp1 cp2 end` for a shared edge.
 *
 * `reversed` means we're traversing the edge opposite to its stored
 * direction; swapping the two control points exactly reproduces the
 * same geometric curve drawn from the other end.
 */
function cubicEdgeSvg(from, to, edge, reversed) {
  const origStart = reversed ? to : from;
  const origEnd = reversed ? from : to;

  const dx = origEnd.x - origStart.x;
  const dy = origEnd.y - origStart.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;

  const cp1x = origStart.x + dx / 3 + edge.cp1Off * nx;
  const cp1y = origStart.y + dy / 3 + edge.cp1Off * ny;
  const cp2x = origStart.x + (2 * dx) / 3 + edge.cp2Off * nx;
  const cp2y = origStart.y + (2 * dy) / 3 + edge.cp2Off * ny;

  if (reversed) {
    return ` C ${f(cp2x)} ${f(cp2y)} ${f(cp1x)} ${f(cp1y)} ${f(to.x)} ${f(to.y)}`;
  }
  return ` C ${f(cp1x)} ${f(cp1y)} ${f(cp2x)} ${f(cp2y)} ${f(to.x)} ${f(to.y)}`;
}

